// Preconfigured automations — speak in designer + client language.
// CLAUDE.md §1: every flow definition lives here, no copy inline.
// The technical wiring (which connector, which protocol) stays in
// component code; this file is what a designer reads.

export type FlowStepKind =
  | "trigger"
  | "automation"
  | "agent"
  | "memory"
  | "moodboard"
  | "catalog"
  | "rule"
  | "approval"
  | "out";

export interface FlowStep {
  id: string;
  kind: FlowStepKind;
  label: string;
  sub?: string;
}

export interface PreconfiguredFlow {
  id: string;
  name: string;
  group: "Leads" | "Project flow" | "Design" | "Money" | "Client chats";
  trigger: string;
  one_liner: string;
  steps: readonly FlowStep[];
  // External apps that participate. Names must match entries in
  // `agenticIntegrations.ts` so the chip renderer can pull initials + tone.
  apps: readonly string[];
  // Subset of `apps` that MUST be connected for the flow to fire.
  // The agent refuses politely (with a "connect X first" prompt) when
  // any required app is disconnected.
  required: readonly string[];
  // Backing n8n workflow id. Operators click through to the editor;
  // the cockpit never embeds the n8n UI (license cleanliness + UX clarity).
  n8nWorkflowId: string;
}

export const PRECONFIGURED_FLOWS: readonly PreconfiguredFlow[] = [
  {
    id: "lead-from-email",
    name: "New enquiry → typed brief",
    group: "Leads",
    trigger: "Email arrives",
    one_liner: "We read the email, set up the project, and draft your warm-welcome reply.",
    apps: ["Gmail", "Outlook", "HubSpot", "Pipedrive"],
    required: ["Gmail"],
    n8nWorkflowId: "fm-lead-from-email",
    steps: [
      { id: "1", kind: "trigger",    label: "Email",         sub: "client enquiry" },
      { id: "2", kind: "automation", label: "Auto-import",   sub: "city · BHK · style" },
      { id: "3", kind: "agent",      label: "Copilot",       sub: "drafts the reply" },
      { id: "4", kind: "memory",     label: "Memory",        sub: "client preferences" },
      { id: "5", kind: "approval",   label: "Maya reviews",  sub: "approve / edit" },
      { id: "6", kind: "out",        label: "Reply sent",    sub: "" },
    ],
  },
  {
    id: "stale-approval-nudge",
    name: "Quiet approvals → gentle nudge",
    group: "Project flow",
    trigger: "Daily at 9 am",
    one_liner: "Approvals older than five days get a kind, project-aware follow-up.",
    apps: ["Pipedrive", "HubSpot", "Gmail", "Slack"],
    required: ["Gmail"],
    n8nWorkflowId: "fm-stale-approval-nudge",
    steps: [
      { id: "1", kind: "trigger", label: "Daily 09:00", sub: "" },
      { id: "2", kind: "agent",   label: "Copilot",     sub: "walks approvals" },
      { id: "3", kind: "rule",    label: "5-day rule",  sub: "fires" },
      { id: "4", kind: "memory",  label: "Memory",      sub: "prior wording" },
      { id: "5", kind: "approval",label: "Maya reviews", sub: "" },
      { id: "6", kind: "out",     label: "Follow-up",    sub: "sent" },
    ],
  },
  {
    id: "bundle-ingest",
    name: "Drop a project file → understood",
    group: "Design",
    trigger: "Floor-plan or BOQ dropped",
    one_liner: "A scene, a BOQ, a plan — we read it and add rooms, items, and budget lines.",
    apps: ["Google Drive", "Dropbox", "AutoCAD (DXF)", "Revit / IFC", "SketchUp"],
    required: ["Google Drive"],
    n8nWorkflowId: "fm-bundle-ingest",
    steps: [
      { id: "1", kind: "trigger",    label: "File drop",    sub: "scene · BOQ · plan" },
      { id: "2", kind: "automation", label: "Auto-import",  sub: "rooms · items" },
      { id: "3", kind: "agent",      label: "Copilot",      sub: "types each item" },
      { id: "4", kind: "catalog",    label: "Catalog",      sub: "matches SKUs" },
      { id: "5", kind: "moodboard",  label: "Moodboard",    sub: "style tags" },
      { id: "6", kind: "out",        label: "Project ready", sub: "" },
    ],
  },
  {
    id: "pre-render-audit",
    name: "Before you render → quick check",
    group: "Design",
    trigger: "You press Take Render",
    one_liner: "Catches a style outlier before you spend on a render the client may push back on.",
    apps: ["Shopify", "WooCommerce", "Notion"],
    required: [],
    n8nWorkflowId: "fm-pre-render-audit",
    steps: [
      { id: "1", kind: "trigger", label: "Take Render", sub: "" },
      { id: "2", kind: "agent",   label: "Copilot",     sub: "style check" },
      { id: "3", kind: "rule",    label: "Style rule",  sub: "vs. Intent" },
      { id: "4", kind: "catalog", label: "Outlier item", sub: "" },
      { id: "5", kind: "approval",label: "Restyle?",     sub: "or render anyway" },
      { id: "6", kind: "out",     label: "Verdict",       sub: "shown live" },
    ],
  },
  {
    id: "milestone-pay",
    name: "Payment in → milestone closed",
    group: "Money",
    trigger: "Payment received",
    one_liner: "A payment moves the project on; the next approval automatically lines up.",
    apps: ["Stripe", "Razorpay", "QuickBooks", "Tally", "DocuSign"],
    required: ["Stripe"],
    n8nWorkflowId: "fm-milestone-pay",
    steps: [
      { id: "1", kind: "trigger",    label: "Payment",       sub: "received" },
      { id: "2", kind: "automation", label: "Reconcile",     sub: "milestone" },
      { id: "3", kind: "agent",      label: "Copilot",       sub: "closes step" },
      { id: "4", kind: "rule",       label: "Next-stage",    sub: "fires" },
      { id: "5", kind: "approval",   label: "Next approval", sub: "queued" },
      { id: "6", kind: "out",        label: "Project moves",  sub: "" },
    ],
  },
  {
    id: "client-chat",
    name: "Client question → grounded reply",
    group: "Client chats",
    trigger: "WhatsApp / Slack message",
    one_liner: "The client gets the same memory-aware answer Maya sees inside the cockpit.",
    apps: ["WhatsApp Business", "Slack", "Telegram", "Microsoft Teams"],
    required: ["WhatsApp Business"],
    n8nWorkflowId: "fm-client-chat",
    steps: [
      { id: "1", kind: "trigger", label: "Client message", sub: "WhatsApp · Slack" },
      { id: "2", kind: "agent",   label: "Copilot",        sub: "reads context" },
      { id: "3", kind: "memory",  label: "Memory",         sub: "past decisions" },
      { id: "4", kind: "catalog", label: "Catalog",        sub: "spec lookups" },
      { id: "5", kind: "rule",    label: "Rule check",     sub: "guards reply" },
      { id: "6", kind: "out",     label: "Reply",          sub: "sent" },
    ],
  },
];

export const FLOW_GROUPS: ReadonlyArray<PreconfiguredFlow["group"]> = [
  "Leads", "Project flow", "Design", "Money", "Client chats",
];

// Apple-style soft pastels per step kind.
export const STEP_TONE: Record<FlowStepKind, string> = {
  trigger:    "bg-rose-50    text-rose-700    border-rose-200",
  automation: "bg-sky-50     text-sky-700     border-sky-200",
  agent:      "bg-foyr/10    text-foyr-deep   border-foyr/40",
  memory:     "bg-violet-50  text-violet-700  border-violet-200",
  moodboard:  "bg-amber-50   text-amber-700   border-amber-200",
  catalog:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  rule:       "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  approval:   "bg-indigo-50  text-indigo-700  border-indigo-200",
  out:        "bg-slate-100  text-slate-700   border-slate-300",
};
